import {
  deleteObject,
  getDownloadURL,
  ref,
  StorageError,
  uploadBytesResumable,
} from "firebase/storage";
import * as Sentry from "@sentry/react";
import { storage } from "./firebase";

/**
 * Ficheros de los briefings de servicios (House of Stories, AI y Digital).
 *
 * A diferencia de los helpers de imágenes de firebaseStorage.ts, aquí no se comprime ni
 * se convierte nada: un .ai, un .psd o un vídeo tienen que llegar tal cual a quien hace
 * el trabajo. Por eso hay un tope de tamaño; lo que lo pase va como enlace (Drive,
 * WeTransfer).
 */
export const MAX_SERVICE_BRIEFING_FILE_BYTES = 100 * 1024 * 1024;

export interface UploadedServiceBriefingFile {
  url: string;
  storagePath: string;
  contentType: string | null;
  sizeBytes: number;
}

/** Quita lo que no debería ir en una ruta de Storage, conservando la extensión. */
const safeFileName = (name: string): string =>
  name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .slice(-120) || "fichero";

export const uploadServiceBriefingFile = (
  file: File,
  serviceBriefingId: string,
  onProgress?: (percent: number) => void
): Promise<UploadedServiceBriefingFile> => {
  if (file.size > MAX_SERVICE_BRIEFING_FILE_BYTES) {
    return Promise.reject(
      new Error(
        `El fichero pesa más de ${
          MAX_SERVICE_BRIEFING_FILE_BYTES / (1024 * 1024)
        } MB; súbelo a Drive o WeTransfer y añade el enlace`
      )
    );
  }

  // El timestamp evita pisar un fichero con el mismo nombre subido antes.
  const storagePath = `service-briefings/${serviceBriefingId}/${Date.now()}-${safeFileName(
    file.name
  )}`;
  const task = uploadBytesResumable(ref(storage, storagePath), file, {
    contentType: file.type || undefined,
  });

  return new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snapshot) => {
        if (onProgress && snapshot.totalBytes > 0) {
          onProgress(
            Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
          );
        }
      },
      (error) => {
        Sentry.captureException(error);
        reject(error);
      },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve({
            url,
            storagePath,
            contentType: file.type || null,
            sizeBytes: file.size,
          });
        } catch (error) {
          Sentry.captureException(error);
          reject(error);
        }
      }
    );
  });
};

/**
 * Borra el fichero de Storage. Si ya no existe no es un error: lo que se quería era que
 * no estuviese.
 */
export const deleteServiceBriefingFile = async (
  storagePath: string
): Promise<void> => {
  try {
    await deleteObject(ref(storage, storagePath));
  } catch (error) {
    if ((error as StorageError)?.code === "storage/object-not-found") return;
    Sentry.captureException(error);
    throw error;
  }
};
